import type { TFunction } from 'i18next';

import type { EIntegrationSyncRule, TIntegrationPersonAttributes } from '@feathr/blackbox';
import { defaultPersonAttributes } from '@feathr/blackbox';
import type { IIntegrationUIMapping } from '@feathr/components';

/** The contact mapping fields the shared mapping logic reads. */
export interface ICrmContactMapping {
  id: string;
  key: string;
  custom_field: string | null;
  default_field: string | null;
  date_last_synced: string | null;
  editable: boolean;
  required: boolean;
  sync_rule: EIntegrationSyncRule;
  status?: 'syncing' | 'stopped';
  stopped_reason?: string | null;
}

/**
 * A change to one contact mapping. A mapping targets a custom field or a default field, so set one
 * and clear the other with `null`.
 */
export interface ICrmContactMappingUpdate {
  customField: string | null;
  defaultField: TIntegrationPersonAttributes | null;
  /** The new direction. Left out to keep the saved one. */
  syncRule?: EIntegrationSyncRule;
  mapping: string;
}

/** CRM-specific mapping behavior. */
export interface ICrmMappingRules<TMapping extends ICrmContactMapping> {
  /** CRM fields the server points at a Feathr field it manages when it creates the mapping. */
  serverBoundKeys?: ReadonlySet<string>;
  /** Every row needs a Feathr field before it can save, because the CRM can't store one without it. */
  requireFeathrField?: boolean;
  /** Why a saved mapping can't be removed, besides being required. */
  getRemoveDisabledReason?: (mapping: TMapping, t: TFunction) => string | undefined;
  /** Whether a saved mapping keeps its Feathr field, besides not being editable. */
  locksFeathrField?: (mapping: TMapping) => boolean;
}

const DEFAULT_FIELD_IDS: ReadonlySet<string> = new Set(defaultPersonAttributes.map(({ id }) => id));

const NO_KEYS: ReadonlySet<string> = new Set();

/** The Feathr field a mapping targets, a default attribute or a custom field id. */
function getFeathrFieldKey(mapping: ICrmContactMapping): string | null {
  return mapping.custom_field ?? mapping.default_field ?? null;
}

/** Why a stopped mapping needs attention. `undefined` while it syncs or after the customer turned it off. */
export function getMappingWarning(
  { status, stopped_reason: stoppedReason }: ICrmContactMapping,
  t: TFunction,
): string | undefined {
  if (status !== 'stopped' || !stoppedReason) {
    return undefined;
  }
  switch (stoppedReason) {
    case 'deleted':
      return t('This field is no longer available, so the mapping stopped syncing.');

    case 'not_writable':
      return t("Feathr can't write to this field, so the mapping stopped syncing.");

    case 'custom_field_missing':
      return t('The Feathr custom field was archived or renamed, so the mapping stopped syncing.');

    default:
      return t('This mapping stopped syncing.');
  }
}

/** A saved mapping as a table row. The row key is the CRM field key. */
export function toUIMapping<TMapping extends ICrmContactMapping>(
  mapping: TMapping,
  t: TFunction,
  { getRemoveDisabledReason }: ICrmMappingRules<TMapping> = {},
): IIntegrationUIMapping {
  return {
    crmFieldKey: mapping.key,
    feathrFieldKey: getFeathrFieldKey(mapping),
    key: mapping.key,
    lastSyncedAt: mapping.date_last_synced,
    removeDisabledReason: mapping.required
      ? t("Required, can't be removed")
      : getRemoveDisabledReason?.(mapping, t),
    syncRule: mapping.sync_rule,
    warning: getMappingWarning(mapping, t),
  };
}

/** The update that points a mapping at a Feathr field, or none, with an optional new direction. */
export function toMappingUpdate(
  mappingId: string,
  feathrFieldKey: string | null,
  syncRule?: EIntegrationSyncRule,
): ICrmContactMappingUpdate {
  const isDefaultField = feathrFieldKey !== null && DEFAULT_FIELD_IDS.has(feathrFieldKey);
  return {
    customField: isDefaultField ? null : feathrFieldKey,
    defaultField: isDefaultField ? (feathrFieldKey as TIntegrationPersonAttributes) : null,
    mapping: mappingId,
    ...(syncRule && { syncRule }),
  };
}

/** A mapping to create, and the Feathr field to point it at once it exists. */
export interface IMappingAddition<TKey extends string = string> {
  key: TKey;
  feathrFieldKey: string | null;
}

/**
 * The requests that turn the saved mappings into the table rows. A mapping moves to a new Feathr
 * field in one update, never a clear and then an assign: a magpie-owned integration deletes a
 * mapping cleared to no field. See `orderMappingUpdates` for the order the assigns run in.
 */
export interface IMappingChanges<TKey extends string = string> {
  remove: string[];
  /** Saved mappings that give up their Feathr field and take none. */
  clear: string[];
  add: Array<IMappingAddition<TKey>>;
  /** Saved mappings that take a new Feathr field, and a new direction if it changed. */
  assign: ICrmContactMappingUpdate[];
  /** Saved mappings that keep their Feathr field and take a new direction. */
  syncRule: ICrmContactMappingUpdate[];
}

/** The follow-up requests for new mappings, which the server can create with a default field. */
export interface IAddedMappingChanges {
  clear: string[];
  assign: ICrmContactMappingUpdate[];
}

/**
 * Locks the Feathr field of a row the integration manages: a saved mapping that isn't editable, or
 * a new one the server points at its own field. A locked row keeps the saved Feathr field.
 */
export function lockManagedFeathrFields<TMapping extends ICrmContactMapping>(
  rows: readonly IIntegrationUIMapping[],
  saved: readonly TMapping[],
  { locksFeathrField, serverBoundKeys = NO_KEYS }: ICrmMappingRules<TMapping> = {},
): IIntegrationUIMapping[] {
  const savedByKey = new Map(saved.map((mapping) => [mapping.key, mapping]));
  return rows.map((row) => {
    const mapping = row.crmFieldKey === null ? undefined : savedByKey.get(row.crmFieldKey);
    const isLocked = mapping
      ? !mapping.editable || !!locksFeathrField?.(mapping)
      : serverBoundKeys.has(row.crmFieldKey ?? '');
    if (isLocked) {
      return {
        ...row,
        feathrFieldKey: mapping ? getFeathrFieldKey(mapping) : null,
        isFeathrFieldLocked: true,
      };
    }
    return row.isFeathrFieldLocked ? { ...row, isFeathrFieldLocked: false } : row;
  });
}

/**
 * The requests that turn the saved mappings into the table rows. Rows match saved mappings by CRM
 * field, which a mapping can't change, so a row moved to another field removes the old mapping
 * and adds a new one. Rows with no CRM field yet are left out. A new direction rides on the
 * Feathr field's update, or on its own update that resends the saved field. Feathr fields the
 * integration manages are never changed.
 */
export function getMappingChanges<TMapping extends ICrmContactMapping>(
  saved: readonly TMapping[],
  rows: readonly IIntegrationUIMapping[],
  serverBoundKeys: ReadonlySet<string> = NO_KEYS,
): IMappingChanges<TMapping['key']> {
  const rowsByField = new Map(
    rows.flatMap((row) => (row.crmFieldKey === null ? [] : [[row.crmFieldKey, row] as const])),
  );
  const savedKeys = new Set<string>(saved.map(({ key }) => key));
  const changes: IMappingChanges<TMapping['key']> = {
    add: [],
    assign: [],
    clear: [],
    remove: [],
    syncRule: [],
  };

  saved.forEach((mapping) => {
    const row = rowsByField.get(mapping.key);
    const savedFeathrFieldKey = getFeathrFieldKey(mapping);
    const syncRule =
      row?.syncRule && row.syncRule !== mapping.sync_rule
        ? (row.syncRule as EIntegrationSyncRule)
        : undefined;
    if (!row) {
      changes.remove.push(mapping.id);
    } else if (mapping.editable && row.feathrFieldKey !== savedFeathrFieldKey) {
      if (row.feathrFieldKey === null) {
        changes.clear.push(mapping.id);
      } else {
        changes.assign.push(toMappingUpdate(mapping.id, row.feathrFieldKey, syncRule));
      }
    } else if (mapping.editable && syncRule && savedFeathrFieldKey !== null) {
      // An update with no Feathr field clears the mapping, which magpie deletes.
      changes.syncRule.push(toMappingUpdate(mapping.id, savedFeathrFieldKey, syncRule));
    }
  });
  rowsByField.forEach((row, key) => {
    if (!savedKeys.has(key)) {
      changes.add.push({
        feathrFieldKey: serverBoundKeys.has(key) ? null : row.feathrFieldKey,
        key: key as TMapping['key'],
      });
    }
  });
  return changes;
}

/**
 * The requests that point new mappings at their staged Feathr fields. The server can create a
 * mapping with a default field; it is kept when the row has none, unless another row takes it.
 */
export function getAddedMappingChanges(
  created: readonly ICrmContactMapping[],
  { add, assign }: Pick<IMappingChanges, 'add' | 'assign'>,
  serverBoundKeys: ReadonlySet<string> = NO_KEYS,
): IAddedMappingChanges {
  const taken = new Set<string>([
    ...assign.flatMap(({ customField, defaultField }) => customField ?? defaultField ?? []),
    ...add.flatMap(({ feathrFieldKey }) => feathrFieldKey ?? []),
  ]);
  const changes: IAddedMappingChanges = { assign: [], clear: [] };

  created.forEach((mapping) => {
    const addition = add.find(({ key }) => key === mapping.key);
    const createdFeathrFieldKey = getFeathrFieldKey(mapping);
    if (!addition || serverBoundKeys.has(mapping.key)) {
      return;
    }
    const target = addition.feathrFieldKey;
    if (createdFeathrFieldKey === target) {
      return;
    }
    if (target !== null) {
      changes.assign.push(toMappingUpdate(mapping.id, target));
    } else if (createdFeathrFieldKey !== null && taken.has(createdFeathrFieldKey)) {
      changes.clear.push(mapping.id);
    }
  });
  return changes;
}

/**
 * Orders the assigns so each takes its Feathr field only after the mapping holding it has moved
 * off. Feathr fields are unique per integration. Mappings that trade fields in a cycle can't be
 * ordered, so the first is cleared to break it, which deletes it on a magpie-owned integration.
 */
export function orderMappingUpdates(
  assign: readonly ICrmContactMappingUpdate[],
  mappings: readonly ICrmContactMapping[],
): ICrmContactMappingUpdate[] {
  const heldFields = new Map(mappings.map((mapping) => [mapping.id, getFeathrFieldKey(mapping)]));
  const pending = [...assign];
  const ordered: ICrmContactMappingUpdate[] = [];
  while (pending.length) {
    const held = new Set(pending.flatMap(({ mapping }) => heldFields.get(mapping) ?? []));
    const index = pending.findIndex(
      ({ customField, defaultField }) => !held.has(customField ?? defaultField ?? ''),
    );
    if (index === -1) {
      ordered.push(toMappingUpdate(pending[0].mapping, null));
      heldFields.set(pending[0].mapping, null);
    } else {
      ordered.push(...pending.splice(index, 1));
    }
  }
  return ordered;
}

export function hasMappingChanges(changes: IMappingChanges): boolean {
  return Object.values(changes).some((list) => list.length > 0);
}
